import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Upload, Sparkles, MessageCircle, ArrowRight } from 'lucide-react';

const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      number: '01',
      icon: Upload,
      title: 'Sube tu información',
      description: 'Carga tu estilo de comunicación, ejemplos de mensajes y el plan de tu paciente. 5 minutos de configuración inicial.',
      detail: 'Documentos, chats anteriores, preferencias de tono',
    },
    {
      number: '02',
      icon: Sparkles,
      title: 'La IA aprende tu voz',
      description: 'HabitIA analiza tu forma de comunicarte y crea un clon que habla exactamente como tú, no como un bot genérico.',
      detail: 'Tu tono, tus frases favoritas, tu empatía',
    },
    {
      number: '03',
      icon: MessageCircle,
      title: 'Acompaña 24/7 por WhatsApp',
      description: 'Tu asistente responde dudas, envía recordatorios, valida fotos de comida y mantiene a tus pacientes en el camino.',
      detail: 'Seguimiento proactivo sin esfuerzo extra',
    },
  ];

  return (
    <section id="como-funciona" ref={ref} className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#849B89] mb-4">
            Cómo funciona
          </span>
          <h2 className="font-cabinet text-3xl md:text-4xl font-bold text-[#1A2E24] tracking-tight mb-4">
            De cero a asistente IA
            <span className="text-[#2E5C41]"> en 3 pasos simples</span>
          </h2>
          <p className="text-base md:text-lg text-[#4B5E53] leading-relaxed">
            Sin código, sin complicaciones. Configura tu clon de IA en menos de 10 minutos.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="relative group"
            >
              {/* Connection arrow */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-8 h-8 text-[#849B89]" strokeWidth={1} />
                </div>
              )}

              <div className="h-full p-8 bg-[#FAFAF9] rounded-3xl border border-[#2E5C41]/10 hover:border-[#2E5C41]/30 hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                {/* Step number */}
                <span className="inline-block text-5xl font-cabinet font-bold text-[#2E5C41]/10 mb-4">
                  {step.number}
                </span>

                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-[#2E5C41] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <step.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>

                {/* Content */}
                <h3 className="font-cabinet text-xl font-bold text-[#1A2E24] mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-[#4B5E53] mb-4 leading-relaxed">
                  {step.description}
                </p>

                {/* Detail badge */}
                <span className="inline-block px-3 py-1.5 bg-[#2E5C41]/5 rounded-full text-xs text-[#2E5C41] font-medium">
                  {step.detail}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Preview section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 md:mt-16 p-6 md:p-8 bg-[#1A2E24] rounded-3xl text-center"
        >
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#2E5C41] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">Vista previa en vivo</p>
                <p className="text-white/60 text-sm">Escucha cómo suena tu IA antes de activarla</p>
              </div>
            </div>
            <div className="h-px w-full md:h-12 md:w-px bg-white/10" />
            <p className="text-white/80 text-sm italic max-w-md">
              "Así suena tu IA ahora: <span className="text-[#849B89]">Hola María, ¿cómo va tu día? Recuerda tu snack de media mañana...</span>"
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
